import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tips } from 'components/Tips';
import { Complain } from 'components/Complain';
import { Footer } from 'components/Footer';
import { PaymentTab } from 'components/PaymentTab';
import { Recipient } from 'components/Recipient';
import { Review } from 'components/Review';
import { useGlobalContext } from 'context/GlobalProvider';
import { useCountryContext } from 'context/CountryProvider';
import { Users } from 'components/Users';
import { Loader } from 'components/Loader';
import {
  EEntryType,
  EEventAction,
  EPaymentTab,
  EReviewTarget,
  ESearchParams,
  EYandexMetrikaEvent,
  paymentTabs,
  UTM_PREFIX,
} from 'common/const';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { useService } from 'common/hooks/useService';
import { useFunnelEntry } from 'common/hooks/useFunnelEntry';
import { IUser } from 'common/types/user.types';
import { getEventPayload } from 'common/helpers/event.helper';
import { sendYandexMetrikaEvent } from 'common/helpers/yandex.helper';
import { getUserName } from 'common/helpers/user.helper';
import { getFunnelParams, getReviewParams } from 'common/helpers/metrika.helper';

export const PaymentLayout: FC = () => {
  const { t } = useTranslation();
  const { getSearchParam, setSearchParam, setParams } = useSearchParamsHook();
  const { organization, setOrganization, user, setUser, activeTab, setActiveTab } = useGlobalContext();
  const { applyOrganizationCountry } = useCountryContext();
  const { Service, loading } = useService();

  const visitorUid = getSearchParam(ESearchParams.VisitorUid);
  const organizationId = getSearchParam(ESearchParams.OrganizationId);
  const userId = getSearchParam(ESearchParams.UserId);
  const review = getSearchParam(ESearchParams.Review);
  const eventPayload = getEventPayload(visitorUid, user?.id);
  const { source, sendLandingOpened } = useFunnelEntry();
  const isReview = review === 'true';
  // Экран оценки официанта или (адрес заведения) заведения; данные получены - есть заведение и, у официанта, он сам
  const reviewTarget = userId ? EReviewTarget.Waiter : EReviewTarget.Organization;
  const reviewParams = getReviewParams(organization, source, reviewTarget);
  const reviewDataReceived = !!organization && (!userId || !!user);
  // Форма чаевых официанта видна гостю: страница официанта, данные получены, посетитель определён
  // (промежуточная форма нового гостя до решения об экране оценки не считается, F-26), не экран оценки,
  // вкладка «Чаевые» активна (metrika-funnel, R-4)
  const checkoutVisible =
    !!userId && !organizationId && !!organization && !!user && !!visitorUid && !isReview && activeTab === EPaymentTab.Tips;
  const reviewShownRef = useRef<boolean>(false);
  const checkoutOpenedRef = useRef<boolean>(false);

  const getOrganization = async (id: string) => {
    const response = await Service.getOrganizationById(id);
    const organization = response?.organization;

    if (organization) {
      // Вход по адресу заведения без source: список официантов на странице оплаты (metrika-landing, R-3)
      sendLandingOpened(organization, EEntryType.EmployeeList);
      applyOrganizationCountry(organization);
      setOrganization(organization);
    }
  };

  const getUser = async (id: string) => {
    const response = await Service.getUserById(id);
    const organization = response?.organization;

    if (organization) {
      const user = organization.users?.[0];

      // Вход по адресу официанта: сразу после ответа, до посетителя, экрана оценки и формы - так
      // landingOpened раньше reviewOpened и checkoutOpened (metrika-landing, R-3, F-16)
      if (user) {
        sendLandingOpened(organization, EEntryType.DirectEmployee);
      }

      applyOrganizationCountry(organization);
      setOrganization(organization);

      if (user) {
        setUser(user);
      }
    }

    if (!visitorUid) {
      await Service.registerVisit(organization?.review_subscription);
    }
  };

  const onUserClick = (user: IUser) => {
    // Выбранный официант - получатель формы: прежний сбрасывается, и форма загружает выбранного по адресу тем же
    // путём, что при первом выборе (ответ об официанте, регистрация посетителя без visitor_uid, экран оценки);
    // до этого после «назад» и выбора другого форма оставалась на первом выбранном (решение владельца 2026-10-30)
    setUser(null);
    // Переход со страницы заведения на форму выбранного официанта - новый внутренний адрес одной записью
    // истории (push, как раньше: «назад» с формы возвращает на страницу заведения со списком): источник
    // переходит параметром source, метки utm_* носителя в него не переходят (R-2)
    setParams(
      (params) => {
        params.delete(ESearchParams.OrganizationId);
        Array.from(params.keys())
          .filter((key) => key.startsWith(UTM_PREFIX))
          .forEach((key) => params.delete(key));
        params.set(ESearchParams.Source, source);
        params.set(ESearchParams.UserId, user.uuid as string);
        return params;
      },
      { replace: false },
    );
  };

  const onDislikeClick = () => {
    Service.sendEvent({ ...eventPayload, action: EEventAction.Dislike });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.DislikeButtonClick, reviewParams);
    setActiveTab(EPaymentTab.Complain);
    setSearchParam(ESearchParams.Review, false);
  };

  const onLeaveTipsClick = () => {
    if (organization?.tips_disabled) {
      alert(t('payment.tips.askForHvalaTerminal'));
      return;
    }
    Service.sendEvent({ ...eventPayload, action: EEventAction.LikeAndTips });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.LikeAndTipButtonClick, reviewParams);
    setSearchParam(ESearchParams.Review, false);
  };

  const getPaymentContentItemClassName = (tab: EPaymentTab) => {
    return `payment-layout__content-item ${tab === activeTab ? 'active' : ''}`;
  };

  // reviewOpened один раз на показ экрана оценки: уход с экрана сбрасывает отметку, перерисовка
  // и восстановление из кэша браузера - нет (эффекты не повторяются, F-13); ответ с ошибкой - события нет (R-12)
  useEffect(() => {
    if (!isReview) {
      reviewShownRef.current = false;
      return;
    }

    if (!reviewShownRef.current && reviewDataReceived && organization) {
      reviewShownRef.current = true;
      sendYandexMetrikaEvent(EYandexMetrikaEvent.ReviewOpened, getReviewParams(organization, source, reviewTarget));
    }
  }, [isReview, reviewDataReceived]);

  // checkoutOpened один раз на загрузку страницы, при первом показе формы гостю (R-4)
  useEffect(() => {
    if (checkoutVisible && organization && !checkoutOpenedRef.current) {
      checkoutOpenedRef.current = true;
      sendYandexMetrikaEvent(EYandexMetrikaEvent.CheckoutOpened, getFunnelParams(organization, source));
    }
  }, [checkoutVisible]);

  useEffect(() => {
    if (organizationId) {
      getOrganization(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (userId && !user) {
      getUser(userId);
    }
  }, [userId, user]);

  if ((!organization || !user) && loading) {
    return <Loader />;
  }

  if (review === 'true') {
    return (
      <div className="payment-layout">
        <Recipient
          userId={userId}
          organizationId={organizationId}
          avatar={userId ? user?.avatar : organization?.avatar}
          name={userId ? getUserName(user?.firstname, user?.lastname) : organization?.name}
          company={userId ? organization?.name : null}
        />

        <Review
          user={user}
          visitorUid={visitorUid}
          redirectUrl={organization?.review_url}
          onDislikeClick={onDislikeClick}
          onLeaveTipsClick={onLeaveTipsClick}
          metrikaParams={reviewParams}
        />

        <Footer />
      </div>
    );
  }

  return (
    <div className="payment-layout">
      <Recipient
        userId={userId}
        organizationId={organizationId}
        avatar={userId ? user?.avatar : organization?.avatar}
        name={userId ? getUserName(user?.firstname, user?.lastname) : organization?.name}
        company={userId ? organization?.name : null}
      />

      <div className="payment-layout__tabs">
        {paymentTabs.map((tab) => {
          return (
            <PaymentTab
              key={tab.id}
              id={tab.id}
              label={t(tab.label)}
              icon={tab.icon}
              checked={tab.id === activeTab}
              onChange={(value) => setActiveTab(value as EPaymentTab)}
            />
          );
        })}
      </div>

      <div className="payment-layout__content">
        <div className={getPaymentContentItemClassName(EPaymentTab.Tips)}>
          {organizationId ? (
            <Users label={t('payment.tips.usersListLabel')} users={organization?.users} onUserClick={onUserClick} />
          ) : (
            <Tips visitorUid={visitorUid} user={user} organization={organization} source={source} />
          )}
        </div>

        <div className={getPaymentContentItemClassName(EPaymentTab.Complain)}>
          {/* Официант жалобы - только на странице официанта: на странице заведения (после «назад») он не выбран,
              хотя в контексте остался прежний (US-044, R-11) */}
          <Complain
            organizationId={organization?.id}
            organizationName={organization?.name}
            uuid={userId ? user?.uuid : undefined}
          />
        </div>
      </div>

      <Footer background="#f6f5fa" />
    </div>
  );
};
